import { useState } from "react";
import { X, Youtube, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { fetchVideoInfo } from "@/lib/youtube-service";
import { useSubscriptionValidation } from "@/hooks/useSubscriptionValidation";

interface YouTubeUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVideoAdd: (videoId: string, title: string, embedUrl: string) => void;
}

// YouTube URL validation and ID extraction
const extractYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

// Validate YouTube URL format
const isValidYouTubeUrl = (url: string): boolean => {
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
  return youtubeRegex.test(url);
};

// Check if video is embeddable by testing the embed URL
const checkVideoEmbeddable = async (videoId: string): Promise<{ isEmbeddable: boolean; title: string }> => {
  try {
    // Try to get video info using YouTube oEmbed API
    const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
    const response = await fetch(oEmbedUrl);
    
    if (response.ok) {
      const data = await response.json();
      return {
        isEmbeddable: true,
        title: data.title || 'YouTube Video'
      };
    } else {
      return {
        isEmbeddable: false,
        title: 'YouTube Video'
      };
    }
  } catch (error) {
    console.error('Error checking video embeddability:', error);
    return {
      isEmbeddable: false,
      title: 'YouTube Video'
    };
  }
};

export const YouTubeUrlModal = ({ isOpen, onClose, onVideoAdd }: YouTubeUrlModalProps) => {
  const [url, setUrl] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();
  const { validateCreditsForAction } = useSubscriptionValidation();

  const handleSubmit = async () => {
    if (!url.trim()) {
      toast({
        title: "Error",
        description: "Please enter a YouTube URL",
        variant: "destructive"
      });
      return;
    }

    if (!isValidYouTubeUrl(url)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid YouTube URL",
        variant: "destructive"
      });
      return;
    }

    const videoId = extractYouTubeVideoId(url);
    if (!videoId) {
      toast({
        title: "Invalid YouTube URL",
        description: "Could not extract video ID from the URL",
        variant: "destructive"
      });
      return;
    }

    // Validate credits before processing video
    const creditsValidation = validateCreditsForAction();
    if (!creditsValidation.canProceed) {
      // Trigger out of credits dialog
      window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog'));
      return;
    }

    setIsValidating(true);

    try {
      // Test if video is accessible using client-side service
      const videoInfo = await fetchVideoInfo(videoId);
      
      if (!videoInfo) {
        toast({
          title: "Video not accessible",
          description: "This video is not accessible or may be private/restricted.",
          variant: "destructive"
        });
        setIsValidating(false);
        return;
      }

      const embedUrl = `https://www.youtube.com/embed/${videoId}`;
      onVideoAdd(videoId, videoInfo.title, embedUrl);
      
      toast({
        title: "Success",
        description: "YouTube video added successfully!"
      });

      // Reset form and close modal
      setUrl("");
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate video';
      
      let userMessage = "Failed to validate video. Please try again.";
      if (errorMessage.includes('CORS') || errorMessage.includes('blocked')) {
        userMessage = 'Unable to access this video due to privacy settings or regional restrictions.';
      } else if (errorMessage.includes('Network error')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast({
        title: "Error",
        description: userMessage,
        variant: "destructive"
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setUrl("");
    setIsValidating(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-slate-900">Add YouTube Video</h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isValidating}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="youtube-url" className="block text-sm font-medium text-slate-700 mb-2">
              YouTube URL
            </label>
            <Input
              id="youtube-url"
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isValidating}
              className="w-full"
            />
          </div>
          
          <div className="text-xs text-slate-500">
            <p>Supported formats:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>https://www.youtube.com/watch?v=VIDEO_ID</li>
              <li>https://youtu.be/VIDEO_ID</li>
              <li>https://www.youtube.com/embed/VIDEO_ID</li>
            </ul>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isValidating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isValidating || !url.trim()}
              className="flex-1"
            >
              {isValidating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                "Add Video"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};